import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  StatusIndicator,
  Alert,
  Badge,
  ProgressBar,
  ColumnLayout,
  FormField,
  Textarea,
  Link,
} from '@cloudscape-design/components';
import { getCleanUsername, getUserAlias } from '../auth/midway';
import { getDataService } from '../services/dataServiceFactory';

const MappingLabels = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMappingIndex, setCurrentMappingIndex] = useState(0);
  const [allMappings, setAllMappings] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [controlDetails, setControlDetails] = useState(new Map());
  const [currentUserId, setCurrentUserId] = useState(null);

  // Parse control key format: Catalog#Version#ControlId
  const parseControlKey = (key) => {
    if (!key) return { catalog: '', version: '', controlId: '' };
    const parts = key.split('#');
    return {
      catalog: parts[0] || '',
      version: parts[1] || '',
      controlId: parts[2] || key,
      full: key,
    };
  };

  // Validate documentation URL
  const isValidDocumentationUrl = (url) => {
    return url && url !== 'NOT_MAPPED' && url.startsWith('http');
  };

  // Load mappings from DynamoDB on component mount
  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    setLoading(true);
    try {
      const userId = await getUserAlias();
      setCurrentUserId(userId);
      
      const data = await getDataService().getAllMappingsForLabeling(userId);
      console.log(`Loaded ${data.length} mappings for labeling`);
      
      // Extract control keys for batch fetching details
      const controlsToFetch = [];
      data.forEach(item => {
        const awsControl = parseControlKey(item.mapping.controlKey);
        const frameworkControl = parseControlKey(item.mapping.mappedControlKey);
        
        controlsToFetch.push({
          frameworkKey: `${awsControl.catalog}#${awsControl.version}`,
          controlKey: item.mapping.controlKey,
        });
        
        controlsToFetch.push({
          frameworkKey: `${frameworkControl.catalog}#${frameworkControl.version}`,
          controlKey: item.mapping.mappedControlKey,
        });
      });
      
      const details = await getDataService().batchGetFrameworkControls(controlsToFetch);
      setControlDetails(details);
      
      setAllMappings(data);
      setError(null);
    } catch (err) {
      console.error('Error loading mappings:', err);
      setError({ 
        type: 'error', 
        message: `Failed to load mappings: ${err.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate progress - count mappings with user feedback (labels)
  const submittedCount = allMappings.filter(m => m.feedback).length;
  const totalCount = allMappings.length;
  const progressPercentage = totalCount > 0 ? (submittedCount / totalCount) * 100 : 0;

  // Get current mapping data
  const currentMappingData = allMappings[currentMappingIndex];
  
  // Get control details for current mapping
  const getControlDetails = (frameworkKey, controlKey) => {
    const lookupKey = `${frameworkKey}#${controlKey}`;
    return controlDetails.get(lookupKey);
  };

  // Parse current mapping if available
  let currentMapping = null;
  let currentSubmission = null;
  
  if (currentMappingData) {
    const awsControlParsed = parseControlKey(currentMappingData.mapping.controlKey);
    const frameworkControlParsed = parseControlKey(currentMappingData.mapping.mappedControlKey);
    
    const awsControlDetails = getControlDetails(
      `${awsControlParsed.catalog}#${awsControlParsed.version}`,
      currentMappingData.mapping.controlKey
    );
    
    const frameworkControlDetails = getControlDetails(
      `${frameworkControlParsed.catalog}#${frameworkControlParsed.version}`,
      currentMappingData.mapping.mappedControlKey
    );
    
    currentMapping = {
      id: currentMappingData.mapping.mappingKey,
      awsControl: {
        type: awsControlParsed.catalog,
        id: awsControlParsed.controlId,
        title: awsControlDetails?.title || awsControlParsed.controlId,
        description: awsControlDetails?.description || 'No description available',
        documentationUrl: awsControlDetails?.documentationUrl,
      },
      frameworkControl: {
        framework: `${frameworkControlParsed.catalog} ${frameworkControlParsed.version}`,
        controlId: frameworkControlParsed.controlId,
        title: frameworkControlDetails?.title || frameworkControlParsed.controlId,
        description: frameworkControlDetails?.description || 'No description available',
      },
    };
    
    // Check if user has already labeled this mapping (from MappingFeedback table)
    if (currentMappingData.feedback) {
      // Parse the labelDecision from the feedback
      const labelDecision = currentMappingData.feedback.labelDecision || 
                            (currentMappingData.feedback.justification?.includes('label-yes') ? 'label-yes' : 'label-no');
      
      currentSubmission = {
        decision: labelDecision === 'label-yes' ? 'yes' : 'no',
        userId: currentMappingData.feedback.reviewerId,
        userName: currentMappingData.feedback.reviewerName,
        timestamp: currentMappingData.feedback.timestamp,
        locked: true,
      };
    }
  }

  // Handle submission
  const handleSubmit = async (decision) => {
    if (!currentMappingData) return;

    try {
      const userId = await getUserAlias();
      const userName = await getCleanUsername();
      
      const mappingDecision = decision === 'yes' ? 'label-yes' : 'label-no';
      const currentFeedback = feedback[currentMapping.id] || '';

      // Submit to DynamoDB
      await getDataService().submitLabel({
        mappingKey: currentMappingData.mapping.mappingKey,
        controlKey: currentMappingData.mapping.controlKey,
        mappedControlKey: currentMappingData.mapping.mappedControlKey,
        reviewerId: userId,
        reviewerName: userName,
        decision: mappingDecision,
        feedback: currentFeedback,
      });

      setError({ 
        type: 'success', 
        message: `Label submitted: ${decision === 'yes' ? 'Mapping is correct' : 'Mapping is incorrect'}` 
      });
      setTimeout(() => setError(null), 3000);

      // Reload mappings to get updated state
      await loadMappings();

      // Auto-advance to next mapping after 1 second
      setTimeout(() => {
        handleNext();
      }, 1000);
    } catch (err) {
      console.error('Error submitting label:', err);
      setError({ type: 'error', message: `Failed to submit label: ${err.message}` });
    }
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (currentMappingIndex > 0) {
      setCurrentMappingIndex(currentMappingIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentMappingIndex < allMappings.length - 1) {
      setCurrentMappingIndex(currentMappingIndex + 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  // Find next unlabeled mapping (check feedback field)
  const handleNextUnlabeled = () => {
    const nextUnlabeledIndex = allMappings.findIndex((mappingData, index) => 
      index > currentMappingIndex && !mappingData.feedback
    );
    
    if (nextUnlabeledIndex !== -1) {
      setCurrentMappingIndex(nextUnlabeledIndex);
    } else {
      setError({ 
        type: 'info', 
        message: 'No more unlabeled mappings found. Great job!' 
      });
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xl">
          <div className="loading-spinner"></div>
          <Box variant="p" margin={{ top: 'm' }}>
            Loading mappings...
          </Box>
        </Box>
      </Container>
    );
  }

  if (!currentMapping) {
    return (
      <Container>
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="success">
            All mappings reviewed!
          </StatusIndicator>
          <Box variant="p" margin={{ top: 'm' }}>
            You've completed labeling all available mappings.
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            description="Help us validate control mappings by indicating whether each framework control correctly maps to an AWS managed control"
          >
            Community Mapping Labels
          </Header>
        }
      >
        {error && (
          <Alert 
            type={error.type || 'error'} 
            dismissible 
            onDismiss={() => setError(null)}
          >
            {error.message || error}
          </Alert>
        )}

        <SpaceBetween size="l">
          {/* Progress Bar */}
          <Box>
            <Box variant="h3" margin={{ bottom: 'xs' }}>
              Progress: {submittedCount} / {totalCount} mappings labeled
            </Box>
            <SpaceBetween size="xs">
              <Box variant="small" color="text-body-secondary">
                {submittedCount} of {totalCount} complete ({Math.round(progressPercentage)}%)
              </Box>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ebed',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#0972d3',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </SpaceBetween>
          </Box>

          {/* Current Mapping Card */}
          <Container>
            <SpaceBetween size="l">
              {/* Question at the top */}
              <Box 
                variant="h2" 
                textAlign="center" 
                padding={{ vertical: 'm' }}
              >
                Does this framework control correctly map to this AWS control?
              </Box>

              {/* Side-by-side controls */}
              <ColumnLayout columns={2} variant="text-grid">
                {/* Framework Control Section */}
                <Box>
                  <SpaceBetween size="m">
                    <Box variant="h2">Framework Control</Box>
                    <SpaceBetween size="s">
                      <Badge color="green">{currentMapping.frameworkControl.framework}</Badge>
                      <Box variant="strong">{currentMapping.frameworkControl.controlId}</Box>
                      <Box variant="h3">{currentMapping.frameworkControl.title}</Box>
                      <Box variant="p" color="text-body-secondary">
                        {currentMapping.frameworkControl.description}
                      </Box>
                    </SpaceBetween>
                  </SpaceBetween>
                </Box>

                {/* AWS Control Section */}
                <Box>
                  <SpaceBetween size="m">
                    <Box variant="h2">AWS Control</Box>
                    <SpaceBetween size="s">
                      <Badge color="blue">{currentMapping.awsControl.type}</Badge>
                      {isValidDocumentationUrl(currentMapping.awsControl.documentationUrl) ? (
                        <Link
                          external
                          href={currentMapping.awsControl.documentationUrl}
                          target="_blank"
                          variant="primary"
                          fontSize="body-m"
                        >
                          <Box variant="strong">{currentMapping.awsControl.id}</Box>
                        </Link>
                      ) : (
                        <Box variant="strong">{currentMapping.awsControl.id}</Box>
                      )}
                      <Box variant="h3">{currentMapping.awsControl.title}</Box>
                      <Box variant="p" color="text-body-secondary">
                        {currentMapping.awsControl.description}
                      </Box>
                    </SpaceBetween>
                  </SpaceBetween>
                </Box>
              </ColumnLayout>

              {/* Actions */}
              <Box>
                {currentSubmission ? (
                  // Show locked submission
                  <Box textAlign="center" padding="l">
                    <SpaceBetween size="m">
                      <StatusIndicator type="success">
                        Your submission: {currentSubmission.decision === 'yes' ? 'Yes, mapping is correct' : 'No, mapping is incorrect'}
                      </StatusIndicator>
                      <Box variant="small" color="text-body-secondary">
                        Submitted on {new Date(currentSubmission.timestamp).toLocaleDateString()} by {currentSubmission.userName}
                      </Box>
                      <Badge color="grey">🔒 Locked</Badge>
                    </SpaceBetween>
                  </Box>
                ) : (
                  // Show action buttons and feedback
                  <SpaceBetween size="m">
                    <SpaceBetween direction="horizontal" size="l">
                      <Button
                        variant="primary"
                        iconName="thumbs-up-filled"
                        onClick={() => handleSubmit('yes')}
                        fullWidth
                      >
                        Yes, this mapping is correct
                      </Button>
                      <Button
                        variant="normal"
                        iconName="thumbs-down-filled"
                        onClick={() => handleSubmit('no')}
                        fullWidth
                      >
                        No, this mapping is incorrect
                      </Button>
                    </SpaceBetween>
                    
                    <FormField
                      label="Feedback (Optional)"
                      description="Provide any additional comments or feedback about this mapping"
                    >
                      <Textarea
                        value={feedback[currentMapping.id] || ''}
                        onChange={({ detail }) => 
                          setFeedback(prev => ({
                            ...prev,
                            [currentMapping.id]: detail.value
                          }))
                        }
                        placeholder="Enter your feedback here..."
                        rows={3}
                      />
                    </FormField>
                  </SpaceBetween>
                )}
              </Box>
            </SpaceBetween>
          </Container>

          {/* Navigation Controls */}
          <Box>
            <SpaceBetween direction="horizontal" size="s">
              <Button
                onClick={handlePrevious}
                disabled={currentMappingIndex === 0}
                iconName="angle-left"
              >
                Previous
              </Button>
              
              <Button
                onClick={handleSkip}
                disabled={currentMappingIndex === allMappings.length - 1}
              >
                Skip
              </Button>

              <Button
                onClick={handleNextUnlabeled}
                iconName="search"
              >
                Next Unlabeled
              </Button>

              <Button
                onClick={handleNext}
                disabled={currentMappingIndex === allMappings.length - 1}
                iconAlign="right"
                iconName="angle-right"
              >
                Next
              </Button>
            </SpaceBetween>

            <Box variant="small" color="text-body-secondary" margin={{ top: 's' }}>
              Viewing mapping {currentMappingIndex + 1} of {totalCount}
            </Box>
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default MappingLabels;
